import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import CustomDataTable from "../CustomDataTable";
import { useCompactLayout } from "./useCompactViewport";
import {
  LIST_PAGE_SIZE_ALL,
  ListToolbarPagination,
  type ListSortOption,
} from "./ListToolbarPagination";

export type ResponsiveDataListProps<T> = {
  data: T[];
  total?: number;
  loading?: boolean;
  columns?: any[];
  countPerPage?: number;
  dataFetchApi?: (...args: any[]) => void;
  isServerSide?: boolean;
  sortServer?: boolean;
  sortOptions?: ListSortOption[];
  noDataComponent?: React.ReactNode;
  conditionalRowStyles?: any;
  selectableRows?: boolean;
  selectableRowsNoSelectAll?: boolean;
  onSelectedRowsChange?: (selected: {
    allSelected: boolean;
    selectedCount: number;
    selectedRows: T[];
  }) => void;
  getRowId?: (row: T) => string | number;
  renderCard: (row: T, ctx: {
    selected: boolean;
    onSelectChange: (checked: boolean) => void;
  }) => React.ReactNode;
  cardGridColumns?: {
    xs?: string;
    sm?: string;
    md?: string;
    lg?: string;
    xl?: string;
  };
  selectedRetreat?: string | number;
  committeeId?: string | number | null;
  parishValue?: any;
  forCouple?: boolean;
  pagination?: boolean;
  title?: string;
  onRowClicked?: (row: T) => void;
  pointerOnHover?: boolean;
  highlightOnHover?: boolean;
  /** Force cards at all widths (e.g. closing section). */
  forceCompact?: boolean;
};

function compareSortValues(a: unknown, b: unknown): number {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  if (typeof a === "number" && typeof b === "number") return a - b;
  return String(a).localeCompare(String(b), undefined, {
    numeric: true,
    sensitivity: "base",
  });
}

export function ResponsiveDataList<T>({
  data = [],
  total = 0,
  loading = false,
  columns = [],
  countPerPage = 10,
  dataFetchApi,
  isServerSide = true,
  sortServer = false,
  sortOptions,
  noDataComponent = (
    <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
      There are no records to display
    </Typography>
  ),
  conditionalRowStyles,
  selectableRows = false,
  selectableRowsNoSelectAll = false,
  onSelectedRowsChange,
  getRowId = (row: any) => row?.id,
  renderCard,
  cardGridColumns = {
    xs: "1fr",
    sm: "1fr",
    md: "repeat(2, minmax(0, 1fr))",
  },
  selectedRetreat = "",
  committeeId = null,
  parishValue = null,
  forCouple = false,
  pagination = true,
  title = "",
  onRowClicked,
  pointerOnHover = false,
  highlightOnHover = false,
  forceCompact = false,
}: ResponsiveDataListProps<T>) {
  const { containerRef, isCompact } = useCompactLayout(forceCompact);

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(countPerPage);
  const [sortBy, setSortBy] = useState<Record<string, unknown>>({});
  const [selectedIds, setSelectedIds] = useState<Set<string | number>>(new Set());
  const prevPageRef = useRef<number | undefined>();

  const derivedSortOptions = useMemo(() => {
    if (sortOptions?.length) return sortOptions;
    if (!columns?.length) return [];
    return columns
      .filter((c) => c?.sortable && c?.id)
      .map((c) => ({
        id: String(c.id),
        label: typeof c.name === "string" ? c.name : String(c.id),
      }));
  }, [columns, sortOptions]);

  const compactSortEnabled = sortServer || !isServerSide;
  const compactTotal = isServerSide ? total : total || data.length;

  const compactRows = useMemo(() => {
    if (isServerSide) return data;
    let rows = data;
    const colId = sortBy.sortColumn as string | undefined;
    const dir = ((sortBy.sortDirection as 1 | -1) || 1) as 1 | -1;
    if (colId) {
      const col = columns.find((c) => String(c.id) === String(colId));
      if (col?.selector) {
        rows = [...data].sort(
          (a, b) => dir * compareSortValues(col.selector(a), col.selector(b)),
        );
      }
    }
    const start = (page - 1) * perPage;
    return rows.slice(start, start + perPage);
  }, [columns, data, isServerSide, page, perPage, sortBy]);

  useEffect(() => {
    if (!isCompact || !dataFetchApi || !isServerSide) return;
    if (prevPageRef.current === undefined) {
      prevPageRef.current = page;
      return;
    }
    if (forCouple) {
      dataFetchApi(page, perPage, null, sortBy, selectedRetreat, parishValue);
    } else {
      dataFetchApi(
        page,
        perPage,
        null,
        sortBy,
        selectedRetreat,
        "",
        committeeId,
        parishValue,
      );
    }
    prevPageRef.current = page;
  }, [page, perPage, sortBy, isCompact]);

  useEffect(() => {
    if (isServerSide) return;
    const maxPage = Math.max(
      1,
      Math.ceil(compactTotal / Math.max(perPage, 1)),
    );
    if (page > maxPage) setPage(maxPage);
  }, [compactTotal, isServerSide, page, perPage]);

  useEffect(() => {
    setSelectedIds(new Set());
  }, [data]);

  const emitSelection = (next: Set<string | number>) => {
    setSelectedIds(next);
    if (!onSelectedRowsChange) return;
    const selectedRows = data.filter((row) => next.has(getRowId(row)));
    onSelectedRowsChange({
      allSelected: data.length > 0 && selectedRows.length === data.length,
      selectedCount: selectedRows.length,
      selectedRows,
    });
  };

  return (
    <Box
      ref={containerRef}
      sx={{
        width: "100%",
        minWidth: 0,
        maxWidth: "100%",
        overflow: "hidden",
      }}
    >
      {!isCompact ? (
        <Box sx={{ width: "100%", maxWidth: "100%", overflowX: "auto" }}>
          <CustomDataTable
          data={data}
          total={total}
          loading={loading}
          columns={columns}
          countPerPage={countPerPage}
          dataFetchApi={dataFetchApi}
          isServerSide={isServerSide}
          sortServer={sortServer}
          noDataComponent={noDataComponent}
          conditionalRowStyles={conditionalRowStyles}
          selectableRows={selectableRows}
          selectableRowsNoSelectAll={selectableRowsNoSelectAll}
          onSelectedRowsChange={onSelectedRowsChange}
          selectedRetreat={selectedRetreat}
          committeeId={committeeId}
          parishValue={parishValue}
          forCouple={forCouple}
          pagination={pagination}
          title={title}
          onRowClicked={onRowClicked}
          pointerOnHover={pointerOnHover}
          highlightOnHover={highlightOnHover}
        />
        </Box>
      ) : (
        <Box>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={28} sx={{ color: "error.main" }} />
            </Box>
          ) : compactRows.length === 0 && compactTotal === 0 ? (
            <Box sx={{ py: 1 }}>{noDataComponent}</Box>
          ) : (
            <Box
              sx={{
                p: { xs: 1.25, sm: 2 },
                display: "grid",
                gridTemplateColumns: cardGridColumns,
                gap: { xs: 1.25, sm: 2 },
              }}
            >
              {compactRows.map((row) => {
                const id = getRowId(row);
                return (
                  <Box key={String(id)} sx={{ minWidth: 0 }}>
                    {renderCard(row, {
                      selected: selectedIds.has(id),
                      onSelectChange: (checked) => {
                        const next = new Set(selectedIds);
                        if (checked) next.add(id);
                        else next.delete(id);
                        emitSelection(next);
                      },
                    })}
                  </Box>
                );
              })}
            </Box>
          )}
          {pagination && (
            <ListToolbarPagination
              page={page}
              pageSize={perPage}
              total={compactTotal}
              onPageChange={(next) => setPage(next)}
              onPageSizeChange={(size) => {
                const nextSize = size === LIST_PAGE_SIZE_ALL ? 10000 : size;
                setPerPage(nextSize);
                setPage(1);
              }}
              sortColumn={
                (sortBy.sortColumn as string) || derivedSortOptions[0]?.id
              }
              sortDirection={(sortBy.sortDirection as 1 | -1) || -1}
              sortOptions={
                compactSortEnabled ? derivedSortOptions : undefined
              }
              onSortChange={
                compactSortEnabled
                  ? (col, dir) => {
                      setSortBy({ sortColumn: col, sortDirection: dir });
                      setPage(1);
                    }
                  : undefined
              }
              selectable={selectableRows}
              allSelected={
                data.length > 0 &&
                data.every((row) => selectedIds.has(getRowId(row)))
              }
              someSelected={
                data.some((row) => selectedIds.has(getRowId(row))) &&
                !data.every((row) => selectedIds.has(getRowId(row)))
              }
              onToggleSelectAll={(checked) => {
                if (selectableRowsNoSelectAll && checked) return;
                const next = new Set(selectedIds);
                data.forEach((row) => {
                  const id = getRowId(row);
                  if (checked) next.add(id);
                  else next.delete(id);
                });
                emitSelection(next);
              }}
            />
          )}
        </Box>
      )}
    </Box>
  );
}
